import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
    Box,
    Typography,
    Tabs,
    Tab,
    Paper,
    Container,
    Fade,
    useTheme,
    CircularProgress,
    Snackbar,
    Alert,
} from '@mui/material';
import {
    Analytics as AnalyticsIcon,
    BarChart as ForecastsIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useKeycloak } from '@react-keycloak/web';

// Import the existing page components
import DataAnalyticsPage from './DataAnalyticsPage';
import ForecastsPage from './ForecastsPage';
import DisaggregatedForecastsPage from './DisaggregatedForecastsPage';

// Utility function to group data by date
function groupByDate(data, keysToSum = ['production', 'consumption']) {
    if (!Array.isArray(data)) return [];
    const grouped = {};
    data.forEach(item => {
        if (!item.timestamp) return;
        const date = new Date(item.timestamp).toISOString().split('T')[0];
        if (!grouped[date]) {
            grouped[date] = { date, count: 0 };
            keysToSum.forEach(key => grouped[date][key] = 0);
        }
        keysToSum.forEach(key => {
            grouped[date][key] += item[key] || 0;
        });
        grouped[date].count += 1;
    });
    return Object.values(grouped);
}

const UnifiedDashboard = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const { method, section } = useParams();
    const { keycloak, initialized } = useKeycloak();

    // Determine the active tab based on the URL
    const getActiveTab = () => {
        if (section === 'forecasts') return 1;
        return 0; // default to analytics
    };

    const [activeTab, setActiveTab] = useState(getActiveTab());
    const [selectedOption, setSelectedOption] = useState('');
    const [selectedUserId, setSelectedUserId] = useState('');
    const [selectedCountry, setSelectedCountry] = useState('');
    const [selectedModelName, setSelectedModelName] = useState('dis'); // Default to 'dis' as fallback
    const [loading, setLoading] = useState(true);

    const [analyticsData, setAnalyticsData] = useState(null);
    const [forecastsAPIData, setForecastsAPIData] = useState(null);
    const [chartsData, setChartsData] = useState(null);

    // Separate loading states for analytics and forecasts
    const [analyticsDataLoading, setAnalyticsDataLoading] = useState(true);
    const [forecastsDataLoading, setForecastsDataLoading] = useState(true);
    const [analyticsDataError, setAnalyticsDataError] = useState(null);
    const [forecastsDataError, setForecastsDataError] = useState(null);

    // State from CalculateFlexibility navigation
    const [navigationState, setNavigationState] = useState(null);

    // Model training status tracking
    const [modelTrainingStatus, setModelTrainingStatus] = useState('unknown');
    const [modelTrainingError, setModelTrainingError] = useState('');
    const [trainingStatusChecked, setTrainingStatusChecked] = useState(false);

    // New states for sequential processing control
    const [isExistingModelWithExistingData, setIsExistingModelWithExistingData] = useState(false);
    const [analyticsCompletedSuccessfully, setAnalyticsCompletedSuccessfully] = useState(false);
    const [shouldStartModelTraining, setShouldStartModelTraining] = useState(false);

    // Notification state for forecasts ready
    const [showForecastsReadyNotification, setShowForecastsReadyNotification] = useState(false);

    // Use nullish coalescing to avoid fallback to 3 when flexibilityDays is 0
    let flexibilityDays = location.state?.flexibilityDays ?? Number(localStorage.getItem('flexibilityDays')) ?? 3;
    // If flexibilityDays is 0, set it to 1
    if (flexibilityDays === 0) {
        flexibilityDays = 1;
    }
    // Persist flexibilityDays in localStorage for reloads
    if (location.state?.flexibilityDays !== undefined) {
        localStorage.setItem('flexibilityDays', location.state.flexibilityDays);
    }

    // Get selectedDevices from navigation state or localStorage, with fallback to default appliances
    let selectedDevices = location.state?.selectedDevices ?? JSON.parse(localStorage.getItem('selectedDevices') || 'null');
    // If no devices are selected, use default appliances as fallback
    if (!selectedDevices || selectedDevices.length === 0) {
        selectedDevices = ["TV", "Dish_Washer", "Washing_Machine", "Washer_Dryer"];
    }
    // Persist selectedDevices in localStorage for reloads
    if (location.state?.selectedDevices !== undefined) {
        localStorage.setItem('selectedDevices', JSON.stringify(location.state.selectedDevices));
    }

    // Effect to get selectedUserId and selectedCountry from localStorage
    useEffect(() => {
        let hasChanges = false;

        // Set user ID from Keycloak token when available, ensuring it's always up-to-date.
        if (initialized && keycloak.tokenParsed?.user_id) {
            const userId = location.state?.userId?.toString() || keycloak.tokenParsed.user_id;
            if (selectedUserId !== userId) {
                setSelectedUserId(userId);
                localStorage.setItem('selectedUserId', userId);
                hasChanges = true;
            }
        } else if (location.state?.userId) {
            // Handle case where we navigate from calculation before keycloak is initialized
            const userId = location.state.userId.toString();
            if (selectedUserId !== userId) {
                setSelectedUserId(userId);
                localStorage.setItem('selectedUserId', userId);
                hasChanges = true;
            }
        } else if (!selectedUserId) {
            // Fallback to localStorage while keycloak is initializing on page refresh
            const fallbackUserId = localStorage.getItem('selectedUserId') || '1';
            setSelectedUserId(fallbackUserId);
            hasChanges = true;
        }

        // Handle country selection
        const countryFromStorage = localStorage.getItem('selectedCountry') || 'Greece';
        const country = location.state?.country || countryFromStorage;
        if (selectedCountry !== country) {
            setSelectedCountry(country);
            hasChanges = true;
        }
        if (location.state?.country && location.state.country !== localStorage.getItem('selectedCountry')) {
            localStorage.setItem('selectedCountry', location.state.country);
        }

        // Handle model name selection with robust defaults
        const modelNameFromStorage = localStorage.getItem('selectedModelName');
        const modelName = location.state?.modelName || modelNameFromStorage;

        if (modelName && selectedModelName !== modelName) {
            setSelectedModelName(modelName);
            localStorage.setItem('selectedModelName', modelName);
            console.log('Using model name from navigation/storage:', modelName);
            hasChanges = true;
        } else if (!modelName || selectedModelName === '') {
            // Set default model names based on method if no specific model is provided
            // or if current selectedModelName is empty
            let defaultModelName;
            if (selectedOption.toLowerCase() === 'disaggregated') {
                defaultModelName = 'dis';
            } else if (selectedOption.toLowerCase() === 'aggregated') {
                defaultModelName = 'M1';
            } else {
                // Fallback when selectedOption is not yet determined
                defaultModelName = 'dis'; // Use 'dis' as ultimate fallback
            }

            if (selectedModelName !== defaultModelName) {
                setSelectedModelName(defaultModelName);
                localStorage.setItem('selectedModelName', defaultModelName);
                console.log('Using default model name:', defaultModelName);
                hasChanges = true;
            }
        }

        // Handle state passed from CalculateFlexibility (only log once)
        if (location.state && !navigationState) {
            setNavigationState(location.state);
            console.log('Received state from Calculate Flexibility:', location.state);

            // Set flag to indicate user has accessed dashboard through proper flow
            localStorage.setItem('dashboardAccessed', 'true');

            // Determine if this is an existing model with existing data (simple case)
            const isFreshCalculation = location.state.freshCalculation;
            const isExistingData = !isFreshCalculation;
            setIsExistingModelWithExistingData(isExistingData);

            if (isExistingData) {
                console.log('SIMPLE CASE: Existing model with existing data - will load analytics then forecasts');
            } else {
                console.log('COMPLEX CASE: New model or new data - will load analytics, then train model, then forecasts');
            }

            // If coming from fresh calculation, show success message briefly
            if (location.state.freshCalculation) {
                console.log('Fresh calculation started successfully!');

                // If model is still processing, show appropriate messaging
                if (location.state.modelProcessing) {
                    console.log('Model is still being processed in the background');
                }
            }
        } else if (!navigationState && !location.state) {
            // If accessing dashboard directly (not from CalculateFlexibility) but dashboard was previously accessed
            const wasAccessed = localStorage.getItem('dashboardAccessed');
            if (wasAccessed === 'true') {
                console.log('Direct dashboard access - user has previously accessed dashboard through proper flow');
                // Allow access since user has previously accessed dashboard properly
            } else {
                console.log('Direct dashboard access without prior proper flow - redirecting to home');
                // Optionally redirect to home or show message
                // navigate('/');
            }
        }
    }, [location.state, initialized, keycloak, selectedOption, selectedUserId, selectedCountry, selectedModelName, navigationState]);

    // Effect to get selectedOption from localStorage and update based on method param
    useEffect(() => {
        const optionFromParam = method ? method.charAt(0).toUpperCase() + method.slice(1) : null;
        const optionFromStorage = localStorage.getItem('selectedOption');
        const currentOption = optionFromParam || optionFromStorage || 'Aggregated';

        setSelectedOption(currentOption);

        if (optionFromParam) {
            localStorage.setItem('selectedOption', optionFromParam);
        } else if (!optionFromStorage && !optionFromParam) {
            localStorage.setItem('selectedOption', 'Aggregated'); // Default if nothing is set
        }

        // Update model name default when option changes if no specific model is stored
        const storedModelName = localStorage.getItem('selectedModelName');
        if (!storedModelName && !location.state?.modelName) {
            let defaultModelName;
            if (currentOption.toLowerCase() === 'disaggregated') {
                defaultModelName = 'dis';
            } else if (currentOption.toLowerCase() === 'aggregated') {
                defaultModelName = 'M1';
            } else {
                // Fallback for any other case
                defaultModelName = 'dis';
            }
            setSelectedModelName(defaultModelName);
            localStorage.setItem('selectedModelName', defaultModelName);
            console.log('Updated default model name for option change:', defaultModelName);
        }
    }, [method, location.state?.modelName]);

    // Effect to monitor model training status
    useEffect(() => {
        const checkTrainingStatus = () => {
            const status = localStorage.getItem('modelTrainingStatus');
            const error = localStorage.getItem('modelTrainingError');

            if (status) {
                setModelTrainingStatus(status);
                setModelTrainingError(error || '');
                setTrainingStatusChecked(true);
                console.log('Model training status:', status, error ? `Error: ${error}` : '');
            } else {
                // If no status in localStorage, assume it's an existing session (not fresh calculation)
                setModelTrainingStatus('completed');
                setTrainingStatusChecked(true);
            }
        };

        // Check initially
        checkTrainingStatus();

        // Set up polling to check for status changes when model is being processed
        let statusCheckInterval;
        if (modelTrainingStatus !== 'completed' && modelTrainingStatus !== 'failed' && modelTrainingStatus !== 'unknown') {
            statusCheckInterval = setInterval(() => {
                const status = localStorage.getItem('modelTrainingStatus');
                if (status && status !== modelTrainingStatus) {
                    checkTrainingStatus();
                }

                // Stop polling once training is completed or failed
                if (status === 'completed' || status === 'failed') {
                    clearInterval(statusCheckInterval);
                }
            }, 2000); // Check every 2 seconds
        }

        // Clean up interval on component unmount and clear localStorage if needed
        return () => {
            if (statusCheckInterval) {
                clearInterval(statusCheckInterval);
            }
            // Clean up localStorage when component unmounts, if this was a fresh calculation session
            if (navigationState?.freshCalculation && modelTrainingStatus === 'completed') {
                localStorage.removeItem('modelTrainingStatus');
                localStorage.removeItem('modelTrainingError');
            }
        };
    }, [modelTrainingStatus, navigationState]);

    // --- Effect to Fetch Analytics Data First (Step 3: Load Analytics) ---
    useEffect(() => {
        const fetchAnalyticsData = async () => {
            // Only fetch if selectedOption, selectedUserId, selectedCountry, and selectedModelName are determined
            if (!selectedOption || selectedOption === 'No Option Selected' || !selectedUserId || !selectedCountry || !selectedModelName) {
                return;
            }

            setAnalyticsDataLoading(true);
            setAnalyticsDataError(null);
            setAnalyticsCompletedSuccessfully(false);
            console.log(`STEP 3: Fetching analytics data for method: ${selectedOption}, userId: ${selectedUserId}, country: ${selectedCountry}, model: ${selectedModelName}`);

            try {
                // Initialize data structure with null values and availability flags
                const analyticsResult = {
                    totalConsumption: null,
                    totalProduction: null,
                    duration: null,
                    frequency: selectedOption.toLowerCase() === 'disaggregated' ? 'Per-minute Data' : 'Hourly Data',
                    maxConsumptionDay: null,
                    maxProductionDay: null,
                    avgConsumption: null,
                    avgProduction: null,
                    // Availability flags
                    hasConsumptionData: false,
                    hasProductionData: false,
                    hasMaxData: false,
                    hasAvgData: false,
                    hasDurationData: false,
                    // Granular availability flags
                    hasMaxConsumptionData: false,
                    hasMaxProductionData: false,
                    hasAvgConsumptionData: false,
                    hasAvgProductionData: false
                };

                const chartsResult = {
                    combinedChartData: null,
                    flexibilityData: null,
                    hasCombinedChartData: false,
                    hasFlexibilityData: false
                };

                // Fetch consumption data
                try {
                    const consumptionResponse = await axios.get(
                        `https://flexibility-backend.dedalus.epu.ntua.gr/csv-data/${selectedUserId}/total-consumption`
                    );
                    analyticsResult.totalConsumption = consumptionResponse.data.total_consumption_kWh.toFixed(2);
                    analyticsResult.hasConsumptionData = true;
                    console.log('Consumption data loaded successfully');
                } catch (error) {
                    console.warn('Failed to fetch consumption data:', error.message);
                }

                // Fetch production data
                try {
                    const productionResponse = await axios.get(
                        `https://flexibility-backend.dedalus.epu.ntua.gr/csv-data/${selectedUserId}/total-production`
                    );
                    const totalProduction = productionResponse.data.total_production_kWh || productionResponse.data.total_production;
                    analyticsResult.totalProduction = totalProduction.toFixed(2);

                    // Only consider it as having production data if the total production is greater than 0
                    if (totalProduction > 0) {
                        analyticsResult.hasProductionData = true;
                        console.log('Production data loaded successfully with meaningful values');
                    } else {
                        console.log('Production data loaded but total production is 0 - treating as no production data');
                    }
                } catch (error) {
                    console.warn('Failed to fetch production data:', error.message);
                }

                // Fetch duration data
                try {
                    const durationResponse = await axios.get(
                        `https://flexibility-backend.dedalus.epu.ntua.gr/csv-data/${selectedUserId}/data-duration`
                    );
                    analyticsResult.duration = durationResponse.data.data_duration_days.toFixed(2);
                    analyticsResult.hasDurationData = true;
                    console.log('Duration data loaded successfully');
                } catch (error) {
                    console.warn('Failed to fetch duration data:', error.message);
                }

                // Fetch max data (consumption and production day)
                // Only attempt this if we have consumption data, as the API may fail without proper data
                if (analyticsResult.hasConsumptionData) {
                    try {
                        const maxDataResponse = await axios.get(
                            `https://flexibility-backend.dedalus.epu.ntua.gr/csv-data/${selectedUserId}/max-consumption-production-day`
                        );

                        // Safely check and set max consumption data
                        if (maxDataResponse.data.max_consumption_day && maxDataResponse.data.max_consumption_day.total_consumption_kWh !== undefined) {
                            analyticsResult.maxConsumptionDay = {
                                date: maxDataResponse.data.max_consumption_day.date,
                                total_consumption_kw: maxDataResponse.data.max_consumption_day.total_consumption_kWh
                            };
                            analyticsResult.hasMaxConsumptionData = true;
                        }

                        // Safely check and set max production data
                        if (maxDataResponse.data.max_production_day && maxDataResponse.data.max_production_day.total_production_kWh !== undefined) {
                            analyticsResult.maxProductionDay = {
                                date: maxDataResponse.data.max_production_day.date,
                                total_production_kw: maxDataResponse.data.max_production_day.total_production_kWh
                            };
                            analyticsResult.hasMaxProductionData = true;
                        }

                        // Set flag if we have any max data (consumption or production)
                        if (analyticsResult.hasMaxConsumptionData || analyticsResult.hasMaxProductionData) {
                            analyticsResult.hasMaxData = true;
                        }

                        console.log('Max data loaded successfully');
                    } catch (error) {
                        console.warn('Failed to fetch max consumption/production data:', error.message);

                        // If the API call fails due to data type issues, we'll have no max data
                        // but that's acceptable - the UI will show "N/A" for max values
                    }
                } else {
                    console.log('Skipping max data fetch - no consumption data available');
                }

                // Fetch average data
                try {
                    const avgResponse = await axios.get(
                        `https://flexibility-backend.dedalus.epu.ntua.gr/csv-data/${selectedUserId}/average-daily-values`
                    );

                    // Safely check and set consumption average
                    if (avgResponse.data['Average Daily Consumption (kWh)'] !== undefined &&
                        avgResponse.data['Average Daily Consumption (kWh)'] !== null &&
                        avgResponse.data['Average Daily Consumption (kWh)'] > 0) {
                        analyticsResult.avgConsumption = avgResponse.data['Average Daily Consumption (kWh)'].toFixed(2);
                        analyticsResult.hasAvgConsumptionData = true;
                    }

                    // Safely check and set production average (only if we have production data)
                    if (analyticsResult.hasProductionData &&
                        avgResponse.data['Average Daily Production (kWh)'] !== undefined &&
                        avgResponse.data['Average Daily Production (kWh)'] !== null &&
                        avgResponse.data['Average Daily Production (kWh)'] > 0) {
                        analyticsResult.avgProduction = avgResponse.data['Average Daily Production (kWh)'].toFixed(2);
                        analyticsResult.hasAvgProductionData = true;
                    }

                    // Set flag if we have any average data (consumption or production)
                    if (analyticsResult.hasAvgConsumptionData || analyticsResult.hasAvgProductionData) {
                        analyticsResult.hasAvgData = true;
                    }

                    console.log('Average data loaded successfully');
                } catch (error) {
                    console.warn('Failed to fetch average data:', error.message);
                }

                // Fetch combined chart data
                try {
                    const combinedChartResponse = await axios.get(
                        `https://flexibility-backend.dedalus.epu.ntua.gr/csv-data/${selectedUserId}/`
                    );
                    chartsResult.combinedChartData = combinedChartResponse.data;
                    chartsResult.hasCombinedChartData = true;
                    console.log('Combined chart data loaded successfully');
                } catch (error) {
                    console.warn('Failed to fetch combined chart data:', error.message);
                }

                // Fetch flexibility data (only for Aggregated method)
                if (selectedOption === 'Aggregated') {
                    try {
                        const flexibilityResponse = await axios.get(
                            `https://flexibility-backend.dedalus.epu.ntua.gr/csv-data/${selectedUserId}/last-week-flexibility-efi-stats`
                        );
                        chartsResult.flexibilityData = flexibilityResponse.data;
                        chartsResult.hasFlexibilityData = true;
                        console.log('Flexibility data loaded successfully');
                    } catch (error) {
                        console.warn('Failed to fetch flexibility data:', error.message);
                    }
                }

                setAnalyticsData(analyticsResult);
                setChartsData(chartsResult);
                setAnalyticsCompletedSuccessfully(true);

                console.log('STEP 3 COMPLETED: Analytics data loading completed with available data');

                // For complex case (new model/new data), trigger model training after analytics completes
                if (!isExistingModelWithExistingData && navigationState?.freshCalculation) {
                    console.log('COMPLEX CASE: Analytics completed, preparing to start model training');
                    setShouldStartModelTraining(true);
                }

            } catch (error) {
                console.error('Unexpected error during analytics data fetch:', error);
                setAnalyticsDataError(error);
            } finally {
                setAnalyticsDataLoading(false);
            }
        };

        fetchAnalyticsData();
    }, [selectedOption, selectedUserId, selectedCountry, selectedModelName, isExistingModelWithExistingData, navigationState]);

    // --- Effect to Handle Model Training (Step 4: Train Model) ---
    useEffect(() => {
        const performModelTraining = async () => {
            if (!shouldStartModelTraining || !navigationState?.freshCalculation) {
                return;
            }

            console.log('STEP 4: Starting model training after analytics completion');
            setShouldStartModelTraining(false); // Reset flag

            try {
                const isAggregated = selectedOption === 'Aggregated';
                const isDisaggregated = selectedOption === 'Disaggregated';

                // Validate required data before proceeding
                if (!selectedModelName || !selectedCountry || !selectedOption || !selectedUserId) {
                    throw new Error('Missing required configuration for model training');
                }

                // Update status to training
                localStorage.setItem('modelTrainingStatus', 'training');
                setModelTrainingStatus('training');

                console.log(`Training ${selectedOption.toLowerCase()} model: ${selectedModelName}`);

                // Train the model based on selected method
                let trainResponse;

                if (isAggregated) {
                    console.log('Training aggregated (prosumer) model...');
                    trainResponse = await fetch(
                        `https://flexibility-backend.dedalus.epu.ntua.gr/train-prosumer-model/?user_id=${selectedUserId}&country=${selectedCountry}&model_name=${selectedModelName}`,
                        {
                            method: 'POST',
                            headers: {
                                accept: 'application/json',
                            },
                            body: '',
                        }
                    );
                } else if (isDisaggregated) {
                    const disaggregatedUrl = `https://flexibility-backend.dedalus.epu.ntua.gr/train-disaggregated-forecast?user_id=${selectedUserId}&country=${selectedCountry}&model_name=${selectedModelName}`;
                    console.log('Training disaggregated model...');

                    trainResponse = await fetch(disaggregatedUrl, {
                        method: 'POST',
                        headers: {
                            'accept': 'application/json',
                            'Content-Type': 'application/json',
                        },
                        body: '',
                    });
                }

                if (!trainResponse.ok) {
                    const errorText = await trainResponse.text();
                    console.error('Model training failed:', {
                        status: trainResponse.status,
                        statusText: trainResponse.statusText,
                        errorText: errorText,
                    });
                    const errorMessage = `Failed to train model: ${trainResponse.status} ${trainResponse.statusText} - ${errorText}`;
                    localStorage.setItem('modelTrainingStatus', 'failed');
                    localStorage.setItem('modelTrainingError', errorMessage);
                    setModelTrainingStatus('failed');
                    setModelTrainingError(errorMessage);
                    throw new Error(errorMessage);
                }

                const trainData = await trainResponse.json();
                console.log('STEP 4 COMPLETED: Model trained successfully:', trainData);

                // Update status to completed - this will trigger forecast data fetching
                localStorage.setItem('modelTrainingStatus', 'completed');
                localStorage.setItem('modelTrainingError', '');
                setModelTrainingStatus('completed');
                setModelTrainingError('');
                console.log('Model training completed successfully - forecast data will now be loaded');

            } catch (error) {
                console.error('Error during model training:', error);
                localStorage.setItem('modelTrainingStatus', 'failed');
                localStorage.setItem('modelTrainingError', error.message);
                setModelTrainingStatus('failed');
                setModelTrainingError(error.message);
            }
        };

        performModelTraining();
    }, [shouldStartModelTraining, navigationState, selectedOption, selectedUserId, selectedCountry, selectedModelName]);

    // --- Effect to Fetch Forecasts Data (Step 5: Load Forecasts) ---
    useEffect(() => {
        const fetchForecastsData = async () => {
            // Only fetch if selectedOption, selectedUserId, selectedCountry, and selectedModelName are determined
            if (!selectedOption || selectedOption === 'No Option Selected' || !selectedUserId || !selectedCountry || !selectedModelName) {
                return;
            }

            // For simple case: Only fetch forecasts after analytics completes and if model training is completed
            // For complex case: Only fetch forecasts after model training is completed
            if (isExistingModelWithExistingData) {
                // Simple case: existing model with existing data
                if (!analyticsCompletedSuccessfully || !trainingStatusChecked || (modelTrainingStatus !== 'completed' && modelTrainingStatus !== 'unknown')) {
                    console.log(`SIMPLE CASE: Waiting for analytics completion (${analyticsCompletedSuccessfully}) and model status check (${modelTrainingStatus})`);
                    setForecastsDataLoading(false);
                    return;
                }
                console.log('SIMPLE CASE: Starting forecast data fetch after analytics completion');
            } else {
                // Complex case: new model or new data
                if (!trainingStatusChecked || modelTrainingStatus !== 'completed') {
                    console.log(`COMPLEX CASE: Waiting for model training completion - status: ${modelTrainingStatus}`);
                    setForecastsDataLoading(false);
                    return;
                }
                console.log('COMPLEX CASE: Starting forecast data fetch after model training completion');
            }

            setForecastsDataLoading(true);
            setForecastsDataError(null);
            console.log(`STEP 5: Fetching forecasts data for method: ${selectedOption}, userId: ${selectedUserId}, country: ${selectedCountry}, model: ${selectedModelName}`);
            console.log('Selected devices for API calls:', selectedDevices);

            try {
                // Handle disaggregated vs regular forecast data fetching
                if (selectedOption.toLowerCase() === 'disaggregated') {
                    console.log('Loading disaggregated forecast data...');

                    // Initialize result structure for disaggregated data
                    const disaggregatedResult = {
                        originalForecast: null,
                        disaggregationData: null,
                        lastDayDisaggregationData: null,
                        flexibilityData: null,
                        hasOriginalForecast: false,
                        hasDisaggregationData: false,
                        hasLastDayDisaggregationData: false,
                        hasFlexibilityData: false
                    };

                    // Fetch original disaggregated forecast data
                    try {
                        const disaggregatedResponse = await axios.get(
                            `https://flexibility-backend.dedalus.epu.ntua.gr/csv-data/${selectedUserId}/forecast-disaggregated?country=${selectedCountry}&model_name=${selectedModelName}`
                        );
                        disaggregatedResult.originalForecast = disaggregatedResponse.data;
                        disaggregatedResult.hasOriginalForecast = true;
                        console.log('Original disaggregated forecasts data loaded successfully');
                    } catch (error) {
                        console.error('Failed to fetch original disaggregated forecast data:', error);
                    }

                    // Fetch disaggregated consumption breakdown by appliance
                    try {
                        const disaggregationResponse = await axios.post(
                            `https://flexibility-backend.dedalus.epu.ntua.gr/csv-data/${selectedUserId}/forecast-disaggregated-disaggregation?country=${selectedCountry}&model_name=${selectedModelName}`,
                            selectedDevices,
                            {
                                headers: {
                                    'Content-Type': 'application/json'
                                }
                            }
                        );
                        disaggregatedResult.disaggregationData = disaggregationResponse.data;
                        disaggregatedResult.hasDisaggregationData = true;
                        console.log('Disaggregated consumption breakdown data loaded successfully for devices:', selectedDevices);
                    } catch (error) {
                        console.error('Failed to fetch disaggregated consumption breakdown data:', error);
                    }

                    // Fetch last day disaggregated consumption breakdown by appliance
                    try {
                        const lastDayDisaggregationResponse = await axios.post(
                            `https://flexibility-backend.dedalus.epu.ntua.gr/csv-data/${selectedUserId}/disaggregated-disaggregation-last-day?country=${selectedCountry}&model_name=${selectedModelName}`,
                            selectedDevices,
                            {
                                headers: {
                                    'Content-Type': 'application/json'
                                }
                            }
                        );
                        disaggregatedResult.lastDayDisaggregationData = lastDayDisaggregationResponse.data;
                        disaggregatedResult.hasLastDayDisaggregationData = true;
                        console.log('Last day disaggregated consumption breakdown data loaded successfully for devices:', selectedDevices);
                    } catch (error) {
                        console.error('Failed to fetch last day disaggregated consumption breakdown data:', error);
                    }

                    // Fetch disaggregated flexibility data
                    try {
                        const flexibilityResponse = await axios.post(
                            `https://flexibility-backend.dedalus.epu.ntua.gr/csv-data/${selectedUserId}/forecast-disaggregated-flexibility?country=${selectedCountry}&model_name=${selectedModelName}`,
                            selectedDevices,
                            {
                                headers: {
                                    'Content-Type': 'application/json'
                                }
                            }
                        );
                        disaggregatedResult.flexibilityData = flexibilityResponse.data;
                        disaggregatedResult.hasFlexibilityData = true;
                        console.log('Disaggregated flexibility data loaded successfully for devices:', selectedDevices);
                    } catch (error) {
                        console.error('Failed to fetch disaggregated flexibility data:', error);
                    }

                    // Set the combined result
                    setForecastsAPIData(disaggregatedResult);

                    // Check if we have any data at all
                    if (!disaggregatedResult.hasOriginalForecast && !disaggregatedResult.hasDisaggregationData && !disaggregatedResult.hasLastDayDisaggregationData && !disaggregatedResult.hasFlexibilityData) {
                        setForecastsDataError(new Error('No disaggregated forecast data available'));
                    }
                } else {
                    // Initialize result structure with availability flags
                    const forecastsResult = {
                        dailySummaries: null,
                        nextDayConsumption: null,
                        nextDaysConsumption: null,
                        nextDayProduction: null,
                        nextDaysProduction: null,
                        efiData: {
                            efiResultsAllDays: null,
                            efiDescriptions: null,
                            hoursStatsAllDays: null
                        },
                        // Availability flags
                        hasDailySummaries: false,
                        hasNextDayForecast: false,
                        hasNextDaysForecast: false,
                        hasEfiData: false,
                        hasHoursData: false
                    };

                    // Fetch forecast summary data
                    console.log('Fetching forecast summary data...');
                    try {
                        const forecastSummaryResponse = await axios.get(
                            `https://flexibility-backend.dedalus.epu.ntua.gr/csv-data/${selectedUserId}/day-summary?model_name=${selectedModelName}&country=${selectedCountry}&method=${selectedOption}&days=${flexibilityDays}`
                        );
                        forecastsResult.dailySummaries = forecastSummaryResponse.data['Daily Summaries'];
                        forecastsResult.hasDailySummaries = true;
                        console.log('Forecast summary data loaded successfully');
                    } catch (error) {
                        console.error('Failed to fetch forecast summary data:', error);
                    }

                    // Fetch next day forecast data
                    console.log('Fetching next day forecast data...');
                    try {
                        const nextDayForecastResponse = await axios.get(
                            `https://flexibility-backend.dedalus.epu.ntua.gr/csv-data/${selectedUserId}/forecast-next-days?model_name=${selectedModelName}&country=${selectedCountry}&days=1`
                        );
                        forecastsResult.nextDayConsumption = nextDayForecastResponse.data.forecasted_hourly_stats_next_days;
                        forecastsResult.nextDayProduction = nextDayForecastResponse.data.forecasted_hourly_stats_next_days;
                        forecastsResult.hasNextDayForecast = true;
                        console.log('Next day forecast data loaded successfully');
                    } catch (error) {
                        console.error('Failed to fetch next day forecast data:', error);
                    }

                    // Fetch next X days forecast data
                    console.log('flexibilityDays in UnifiedDashboard:', flexibilityDays);
                    try {
                        const nextDaysForecastResponse = await axios.get(
                            `https://flexibility-backend.dedalus.epu.ntua.gr/csv-data/${selectedUserId}/forecast-next-days?model_name=${selectedModelName}&country=${selectedCountry}&days=${flexibilityDays}`
                        );
                        forecastsResult.nextDaysConsumption = nextDaysForecastResponse.data.forecasted_hourly_stats_next_days;
                        forecastsResult.nextDaysProduction = nextDaysForecastResponse.data.forecasted_hourly_stats_next_days;
                        forecastsResult.hasNextDaysForecast = true;
                        console.log(`Next ${flexibilityDays} days forecast data loaded successfully`);
                    } catch (error) {
                        console.error(`Failed to fetch next ${flexibilityDays} days forecast data:`, error);
                    }

                    // Fetch EFI data
                    console.log('Fetching EFI data...');
                    try {
                        const efiResponse = await axios.get(
                            `https://flexibility-backend.dedalus.epu.ntua.gr/csv-data/${selectedUserId}/efi-in-time-periods?model_name=${selectedModelName}&country=${selectedCountry}&days=${flexibilityDays}`
                        );
                        forecastsResult.efiData.efiResultsAllDays = efiResponse.data['EFI Results for all days'];
                        forecastsResult.efiData.efiDescriptions = efiResponse.data['EFI Descriptions'];
                        forecastsResult.hasEfiData = true;
                        console.log('EFI data loaded successfully');
                    } catch (error) {
                        console.error('Failed to fetch EFI data:', error);
                    }

                    // Fetch hours data
                    console.log('Fetching hours data...');
                    try {
                        const hoursResponse = await axios.get(
                            `https://flexibility-backend.dedalus.epu.ntua.gr/csv-data/${selectedUserId}/forecast-and-identify-hours?model_name=${selectedModelName}&country=${selectedCountry}&days=${flexibilityDays}`
                        );
                        forecastsResult.efiData.hoursStatsAllDays = hoursResponse.data['forecasted_hourly_stats_all_days'];
                        forecastsResult.hasHoursData = true;
                        console.log('Hours data loaded successfully');
                    } catch (error) {
                        console.error('Failed to fetch hours data:', error);
                    }

                    // Set the results even if some calls failed
                    setForecastsAPIData(forecastsResult);
                    console.log('Aggregated forecasts data loading completed with available data');

                    // Check if we have any data at all
                    const hasAnyData = forecastsResult.hasDailySummaries ||
                        forecastsResult.hasNextDayForecast ||
                        forecastsResult.hasNextDaysForecast ||
                        forecastsResult.hasEfiData ||
                        forecastsResult.hasHoursData;

                    if (!hasAnyData) {
                        console.warn('No forecast data was successfully loaded');
                        setForecastsDataError(new Error('No forecast data available'));
                    }
                }

                console.log('STEP 5 COMPLETED: Forecasts data loading completed');

            } catch (error) {
                console.error('Unexpected error during forecasts data fetch:', error);
                setForecastsDataError(error);
            } finally {
                setForecastsDataLoading(false);
            }
        };

        // Start fetching forecasts based on the flow
        fetchForecastsData();
    }, [selectedOption, selectedUserId, selectedCountry, selectedModelName, modelTrainingStatus, trainingStatusChecked, isExistingModelWithExistingData, analyticsCompletedSuccessfully]);

    // Update active tab when URL changes
    useEffect(() => {
        setActiveTab(getActiveTab());
    }, [section]);

    // Redirect to analytics if method is provided but section is not
    useEffect(() => {
        if (method && !section) {
            navigate(`/dashboard/${method}/analytics`, { replace: true });
        }
    }, [method, section, navigate]);

    // Show notification when forecasts data finishes loading successfully
    useEffect(() => {
        // Only show notification if:
        // 1. Forecasts are not currently loading (finished loading)
        // 2. There's no error
        // 3. We have forecast data
        // 4. User is currently on analytics tab (so they can be notified to switch)
        if (!forecastsDataLoading && 
            !forecastsDataError && 
            forecastsAPIData && 
            activeTab === 0) {
            
            // Check if we have any meaningful forecast data
            const hasData = selectedOption.toLowerCase() === 'disaggregated' 
                ? (forecastsAPIData.hasOriginalForecast || 
                   forecastsAPIData.hasDisaggregationData || 
                   forecastsAPIData.hasFlexibilityData)
                : (forecastsAPIData.hasDailySummaries || 
                   forecastsAPIData.hasNextDayForecast || 
                   forecastsAPIData.hasEfiData);
            
            if (hasData) {
                setShowForecastsReadyNotification(true);
            }
        }
    }, [forecastsDataLoading, forecastsDataError, forecastsAPIData, activeTab, selectedOption]);

    // Handle notification close
    const handleNotificationClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setShowForecastsReadyNotification(false);
    };

    const handleTabChange = (event, newValue) => {
        // setActiveTab(newValue); // This will be handled by the useEffect listening to `section`
        const newSection = newValue === 0 ? 'analytics' : 'forecasts';
        const currentMethod = method || selectedOption.toLowerCase() || 'aggregated'; // Ensure method is passed
        navigate(`/dashboard/${currentMethod}/${newSection}`);
    };

    const TabPanel = ({ children, value, index, ...other }) => (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`dashboard-tabpanel-${index}`}
            aria-labelledby={`dashboard-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Fade in={true} timeout={300}>
                    <Box>{children}</Box>
                </Fade>
            )}
        </div>
    );

    const dashboardTabs = [
        {
            label: 'Analytics',
            icon: <AnalyticsIcon />,
            component: <DataAnalyticsPage
                analyticsAPIData={analyticsData}
                chartsAPIData={chartsData}
                selectedDashboardOption={selectedOption}
            />
        },
        {
            label: 'Forecasts',
            icon: <ForecastsIcon />,
            component: selectedOption.toLowerCase() === 'disaggregated'
                ? <DisaggregatedForecastsPage
                    forecastsAPIData={forecastsAPIData}
                    selectedDashboardOption={selectedOption}
                />
                : <ForecastsPage
                    forecastsAPIData={forecastsAPIData}
                    selectedDashboardOption={selectedOption}
                    flexibilityDays={flexibilityDays}
                />
        }
    ];

    return (
        <Container maxWidth={false} disableGutters sx={{ mt: 1, mb: 4 }}>
            {/* Header */}
            <Box sx={{ mb: 4, ml: 4, mr: 4 }}>
                <Typography
                    variant="h3"
                    gutterBottom
                    sx={{
                        fontWeight: 'bold',
                        background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                        lineHeight: 1.3,
                        py: 1,
                        mb: 1,
                    }}
                >
                    Energy Analytics & Forecasting Dashboard
                </Typography>
                {/* Display selected country, option, and model as styled boxes */}
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                    {/* Selected Country */}
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="subtitle1" sx={{ marginRight: 1 }}>
                            Selected Country
                        </Typography>
                        <Box
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                                border: '1px solid rgba(0, 0, 0, 0.1)',
                                borderRadius: '4px',
                                padding: '6px 12px',
                            }}
                        >
                            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                {selectedCountry}
                            </Typography>
                        </Box>
                    </Box>

                    {/* Selected Option */}
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="subtitle1" sx={{ marginRight: 1 }}>
                            Selected Option
                        </Typography>
                        <Box
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                                border: '1px solid rgba(0, 0, 0, 0.1)',
                                borderRadius: '4px',
                                padding: '6px 12px',
                            }}
                        >
                            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                {selectedOption}
                            </Typography>
                        </Box>
                    </Box>

                    {/* Model Name */}
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="subtitle1" sx={{ marginRight: 1 }}>
                            Model Name
                        </Typography>
                        <Box
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                                border: '1px solid rgba(0, 0, 0, 0.1)',
                                borderRadius: '4px',
                                padding: '6px 12px',
                            }}
                        >
                            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                {selectedModelName}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* Navigation Tabs */}
            <Box sx={{ mx: 3 }}>
                <Paper
                    elevation={3}
                    sx={{
                        mb: 3,
                        borderRadius: 3,
                        overflow: 'hidden',
                        background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
                    }}
                >
                    <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        variant="fullWidth"
                        indicatorColor="primary"
                        textColor="primary"
                        sx={{
                            '& .MuiTab-root': {
                                minHeight: { xs: 60, sm: 72 },
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                fontWeight: 500,
                                textTransform: 'none',
                                '&:hover': {
                                    backgroundColor: theme.palette.action.hover,
                                    transition: 'background-color 0.3s ease',
                                },
                            },
                            '& .MuiTabs-indicator': {
                                height: 4,
                                borderRadius: '2px 2px 0 0',
                            },
                        }}
                    >
                        {dashboardTabs.map((tab, index) => (
                            <Tab
                                key={index}
                                icon={tab.icon}
                                label={tab.label}
                                iconPosition="start"
                                sx={{
                                    '& .MuiSvgIcon-root': {
                                        mr: 1,
                                        fontSize: { xs: '1.25rem', sm: '1.5rem' },
                                    },
                                    flexDirection: { xs: 'column', sm: 'row' },
                                }}
                            />
                        ))}
                    </Tabs>
                </Paper>

                {/* Tab Content */}
                {dashboardTabs.map((tab, index) => (
                    <TabPanel key={index} value={activeTab} index={index}>
                        <Paper
                            elevation={2}
                            sx={{
                                borderRadius: 3,
                                overflow: 'hidden',
                                minHeight: '70vh', // Ensure content area is sufficient
                                background: theme.palette.background.paper,
                            }}
                        >
                            {index === 0 ? (
                                // Analytics tab - show loading only for analytics data
                                analyticsDataLoading ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh', mt: 3 }}>
                                        <CircularProgress size={60} />
                                        <Typography sx={{ ml: 2 }}>Loading Analytics Data First...</Typography>
                                    </Box>
                                ) : analyticsDataError && !analyticsData ? (
                                    // Only show error if we have no data at all due to a critical error
                                    <Paper elevation={2} sx={{ p: 3, mt: 3, borderRadius: 3, textAlign: 'center', color: 'error.main' }}>
                                        <Typography variant="h6">Failed to load analytics data.</Typography>
                                        <Typography>{analyticsDataError.message}</Typography>
                                    </Paper>
                                ) : (
                                    // Show the component even if some APIs failed but we have partial data
                                    <>
                                        {analyticsData && (
                                            analyticsData.hasConsumptionData ||
                                            analyticsData.hasProductionData ||
                                            analyticsData.hasDurationData
                                        ) ? (
                                            tab.component
                                        ) : (
                                            <Paper elevation={2} sx={{ p: 3, mt: 3, borderRadius: 3, textAlign: 'center', color: 'warning.main' }}>
                                                <Typography variant="h6">No analytics data available</Typography>
                                                <Typography>The selected dataset does not contain any compatible data for analysis.</Typography>
                                            </Paper>
                                        )}
                                    </>
                                )
                            ) : (
                                // Forecasts tab - show different states based on model training status
                                !trainingStatusChecked || (modelTrainingStatus !== 'completed' && modelTrainingStatus !== 'unknown') ? (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '50vh', mt: 3 }}>
                                        <CircularProgress size={60} />
                                        <Typography sx={{ mt: 2, textAlign: 'center' }}>
                                            {modelTrainingStatus === 'starting' && 'Initializing model creation...'}
                                            {modelTrainingStatus === 'creating' && 'Creating your model...'}
                                            {modelTrainingStatus === 'created' && 'Model created successfully. Loading analytics first, then training will start...'}
                                            {modelTrainingStatus === 'training' && 'Training your model... This may take a few minutes.'}
                                            {modelTrainingStatus === 'failed' && 'Model training failed'}
                                            {!trainingStatusChecked && 'Checking model status...'}
                                        </Typography>
                                        {modelTrainingStatus === 'failed' && modelTrainingError && (
                                            <Typography sx={{ mt: 1, color: 'error.main', textAlign: 'center', maxWidth: '80%' }}>
                                                {modelTrainingError}
                                            </Typography>
                                        )}
                                        {(modelTrainingStatus === 'creating' || modelTrainingStatus === 'created' || modelTrainingStatus === 'training') && (
                                            <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary', textAlign: 'center', maxWidth: '80%' }}>
                                                Your model is being processed. Forecast data will load automatically once training is complete.
                                                You can view the Analytics tab while waiting.
                                            </Typography>
                                        )}
                                    </Box>
                                ) : forecastsDataLoading ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh', mt: 3 }}>
                                        <CircularProgress size={60} />
                                        <Typography sx={{ ml: 2 }}>
                                            Loading Forecasts Data...
                                        </Typography>
                                    </Box>
                                ) : forecastsDataError ? (
                                    <Paper elevation={2} sx={{ p: 3, mt: 3, borderRadius: 3, textAlign: 'center', color: 'error.main' }}>
                                        <Typography variant="h6">Failed to load forecasts data.</Typography>
                                        <Typography>{forecastsDataError.message}</Typography>
                                        {navigationState?.modelProcessing && (
                                            <Typography sx={{ mt: 1, fontStyle: 'italic' }}>
                                                Model may still be processing. Please try again in a few moments.
                                            </Typography>
                                        )}
                                    </Paper>
                                ) : (
                                    tab.component
                                )
                            )}
                        </Paper>
                    </TabPanel>
                ))}
            </Box>

            {/* Forecasts Ready Notification */}
            <Snackbar
                open={showForecastsReadyNotification}
                autoHideDuration={6000}
                onClose={handleNotificationClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert 
                    onClose={handleNotificationClose} 
                    severity="success" 
                    variant="filled"
                    sx={{ width: '100%' }}
                    action={
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Typography 
                                variant="body2" 
                                sx={{ 
                                    color: 'inherit', 
                                    cursor: 'pointer', 
                                    textDecoration: 'underline',
                                    fontWeight: 'bold'
                                }}
                                onClick={() => {
                                    setActiveTab(1);
                                    const currentMethod = method || selectedOption.toLowerCase() || 'aggregated';
                                    navigate(`/dashboard/${currentMethod}/forecasts`);
                                    setShowForecastsReadyNotification(false);
                                }}
                            >
                                View Now
                            </Typography>
                        </Box>
                    }
                >
                    🎉 Forecasts data is ready! Click "View Now" or switch to the Forecasts tab.
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default UnifiedDashboard; 